import React, { useMemo, useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { coordinatorApi } from '../api/endpoints';
import { useTeamOptions } from '../hooks/useTeamOptions';
import { toast } from 'sonner';

const ROLE_BADGE = {
  Admin: 'bg-brand-navy text-white',
  Manager: 'bg-brand-gold-soft text-brand-navy',
  'Sales Agent': 'bg-emerald-100 text-emerald-700',
  Viewer: 'bg-slate-100 text-slate-600',
};

export const CoordinatorDialog = ({ property, trigger, onAssigned }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coordinator, setCoordinator] = useState(property?.coordinator || '');
  const [q, setQ] = useState('');
  const team = useTeamOptions();

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return team.filter(u => !ql || `${u.name} ${u.role}`.toLowerCase().includes(ql));
  }, [team, q]);

  const submit = async () => {
    if (!coordinator) { toast.error('Select a team member first'); return; }
    setSaving(true);
    try {
      await coordinatorApi.assign(property.id, coordinator);
      toast.success(`Assigned ${coordinator} to ${property.name} — existing leads were re-routed.`);
      onAssigned && onAssigned({ ...property, coordinator });
      setOpen(false);
    } catch (e) {
      console.error('Coordinator assignment failed', e);
      toast.error('Failed to assign coordinator');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Assign Team Coordinator</DialogTitle>
          <DialogDescription>
            Choose who handles all incoming leads for <span className="font-semibold text-slate-700">{property?.name}</span>.
            New leads will auto-assign to this coordinator and existing leads on this project will be re-routed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="relative">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Search team members..."
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="coordinator-search"
            />
          </div>
          <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="coordinator-user-list">
            {team.length === 0 && <div className="p-4 text-sm text-slate-400 text-center">Loading team members…</div>}
            {team.length > 0 && filtered.length === 0 && (
              <div className="p-4 text-sm text-slate-400 text-center">No members match "{q}"</div>
            )}
            {filtered.map(u => (
              <button
                key={u.id}
                type="button"
                onClick={() => setCoordinator(u.name)}
                data-testid={`coordinator-option-${u.id}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors ${
                  coordinator === u.name ? 'bg-brand-navy/5' : 'hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  {coordinator === u.name
                    ? <Icons.CheckCircle2 className="w-4 h-4 text-brand-navy shrink-0" />
                    : <Icons.Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                  <span className="text-sm font-medium text-slate-800 truncate">{u.name}</span>
                </span>
                <Badge className={`text-[10px] shrink-0 ${ROLE_BADGE[u.role] || 'bg-slate-100 text-slate-600'}`}>{u.role}</Badge>
              </button>
            ))}
          </div>
          {property?.coordinator && coordinator && property.coordinator !== coordinator && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2 flex gap-2">
              <Icons.AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Currently <strong className="mx-1">{property.coordinator}</strong> handles this project. Reassigning will move all leads to {coordinator}.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-brand-navy hover:bg-[#13294A]" onClick={submit} disabled={saving || !coordinator} data-testid="coordinator-confirm-btn">
            {saving ? 'Assigning...' : 'Confirm Assignment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CoordinatorDialog;
